import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuth must be used within an AuthProvider');

  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const response = await authAPI.verify();

          const userData = response.data.user;
          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');

          setUser(null);
          setIsAuthenticated(false);
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);

      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';

      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);

      const { token, user: newUser } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(newUser));

      setUser(newUser);
      setIsAuthenticated(true);

      return { success: true, user: newUser };
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');

    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(userData);

    localStorage.setItem('user', JSON.stringify(userData));
  };

  const forgotPassword = async (email) => {
    try {
      await authAPI.forgotPassword(email);

      return { success: true };
    } catch (error) {
      const message =
        error.response?.data?.error || 'Password reset request failed';
      
      return { success: false, error: message };
    }
  };

  const updateUserProfileImage = (newImageUrl) => {
    setUser(currentUser => ({
        ...currentUser,
        profileImage: newImageUrl
    }));
  };

  const updateOnboardingStatus = (completed) => {
    setUser(currentUser => ({
      ...currentUser,
      onboardingCompleted: completed
    }));
    const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
    savedUser.onboardingCompleted = completed;
    localStorage.setItem('user', JSON.stringify(savedUser));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    forgotPassword,
    updateUserProfileImage,
    updateOnboardingStatus
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
